import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Users, MessageSquare, ArrowRight } from 'lucide-react'; 
import { format as formatTimeAgo } from 'timeago.js';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import type { Decision, DecisionResponse } from '../types/decision';
import { getResponseStatus } from '../services/functions';
import ResponseActions from './ResponseActions';
import { User } from 'firebase/auth';
import { useParticipant } from '../hooks/useParticipant';
import { ParticipantList } from './ParticipantList';
import Analytics from '../services/analytics';
import { useDecision } from '../App';
import { Pencil } from 'lucide-react';



interface ResponseViewProps {
  user: User | null;
  justCompleted?: boolean;
}

export function ResponseView({ user }: ResponseViewProps) {
  const { decisionId } = useParams<{ decisionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [decision, setDecision] = useState<Decision | null>(null);
  const [response, setResponse] = useState<DecisionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isJustCompleted, setIsJustCompleted] = useState(location.state?.justCompleted ?? false);
  const { participant } = useParticipant(decisionId || '');
  const { setCurrentDecision } = useDecision(); //for metatags
  const [showDetails, setShowDetails] = useState(false);

  

  const startPolling = useCallback(() => {
    if (!decisionId) return;
    
    // Don't check for completion status if we're closing the decision
    // or if the justCompleted flag is set
    if (location.state?.justCompleted && decision?.status !== 'closed') {
      // If the participant hasn't actually completed chat with messages,
      // we shouldn't start polling
      if (participant && participant.status !== 'completed') {
        setIsJustCompleted(false);
        return;
      }
    }
    
    setIsPolling(true);
    setIsRefreshing(true);
    
    console.log('Starting polling for response...');
  
    const pollInterval = setInterval(async () => {
      try {
        const status = await getResponseStatus(decisionId);
        console.log('Polling status:', status);
        
        if (status.status === 'idle' && (status.finalResponseId || status.interimResponseId)) {
          // Always prefer final response if available
          const responseId = status.finalResponseId || status.interimResponseId;
          const responseRef = doc(db, 'decisions', decisionId, 'responses', responseId!);
          const responseSnap = await getDoc(responseRef);
                  
          if (responseSnap.exists()) {
            console.log('Found updated response:', status.finalResponseId ? 'final' : 'interim');
            setResponse(responseSnap.data() as DecisionResponse);
            cleanupPolling();
          }
        } else if (status.status === 'failed') {
          setError('Failed to generate response. Please try again.');
          cleanupPolling();
        }
        // Add timeout after 30 seconds
        else if (Date.now() - startTime > 30000) {
          setError('Response generation timed out. Please try refreshing.');
          cleanupPolling();
        }
      } catch (err) {
        console.error('Error polling response status:', err);
        setError('Failed to check response status. Please try refreshing.');
        cleanupPolling();
      }
    }, 2000);
  
    const startTime = Date.now();
    
    const cleanupPolling = () => {
      clearInterval(pollInterval);
      setIsPolling(false);
      setIsRefreshing(false);
      setIsJustCompleted(false);
      console.log('Polling stopped');
    };
  
    return cleanupPolling;
  }, [decisionId, decision?.status, participant, location.state?.justCompleted]);

  const handleShare = () => {
    if (decisionId) {

      Analytics.trackEvent('share_initiated', { 
        decisionId, 
        fromResponse: true
      });
      
      navigate(`/d/${decisionId}/share`);
    }
  };

  // Function to format message text with markdown-style formatting
const formatMessageText = (text: string): React.ReactNode => {
  // Process bold formatting
  const processBold = (parts: React.ReactNode[]): React.ReactNode[] => {
    return parts.flatMap((part, i) => {
      if (typeof part !== 'string') return part;
      
      const boldParts = part.split(/(\*\*.*?\*\*)/g);
      return boldParts.map((boldPart, index) => {
        if (boldPart.startsWith('**') && boldPart.endsWith('**')) {
          return <strong key={`bold-${i}-${index}`}>{boldPart.slice(2, -2)}</strong>;
        }
        return boldPart;
      });
    });
  };
  
  // Process italics formatting
  const processItalics = (parts: React.ReactNode[]): React.ReactNode[] => {
    return parts.flatMap((part, i) => {
      if (typeof part !== 'string') return part;
      
      const italicParts = part.split(/(\*.*?\*|_.*?_)/g);
      return italicParts.map((italicPart, index) => {
        if ((italicPart.startsWith('*') && italicPart.endsWith('*') && !italicPart.startsWith('**')) || 
            (italicPart.startsWith('_') && italicPart.endsWith('_'))) {
          const cleanText = italicPart.startsWith('*') ? 
            italicPart.slice(1, -1) : italicPart.slice(1, -1);
          return <em key={`italic-${i}-${index}`}>{cleanText}</em>;
        }
        return italicPart;
      });
    });
  };
  
  // Start with the entire text as a single part
  let formattedParts: React.ReactNode[] = [text];
  
  // Apply formatting in sequence
  formattedParts = processBold(formattedParts);
  formattedParts = processItalics(formattedParts);
  
  return formattedParts;
};

  useEffect(() => {
    const loadData = async () => {
      if (!decisionId) return;
  
      try {
        const decisionRef = doc(db, 'decisions', decisionId);
        const decisionSnap = await getDoc(decisionRef);
        
        if (!decisionSnap.exists()) {
          throw new Error('Decision not found');
        }
  
        setDecision({
          id: decisionSnap.id,
          ...decisionSnap.data()
        } as Decision);

        setCurrentDecision({
          id: decisionSnap.id,
          ...decisionSnap.data()
        } as Decision);
  
        const responseStatus = await getResponseStatus(decisionId);
        
        // Try to load final response first if it exists, otherwise load interim
        if (responseStatus.finalResponseId) {
          const responseRef = doc(db, 'decisions', decisionId, 'responses', responseStatus.finalResponseId);
          const responseSnap = await getDoc(responseRef);
          
          if (responseSnap.exists()) {
            // Store the data in a variable first
            const responseData = responseSnap.data() as DecisionResponse; // Added this line
            setResponse(responseData); // Changed to use the variable
            
            Analytics.trackResponseViewed(
              decisionId, 
              'final', 
              responseData.participantCount // Now responseData is defined
            );
          }
        } else if (responseStatus.interimResponseId) {
          const responseRef = doc(db, 'decisions', decisionId, 'responses', responseStatus.interimResponseId);
          const responseSnap = await getDoc(responseRef);
          
          if (responseSnap.exists()) {
            // Store the data in a variable first
            const responseData = responseSnap.data() as DecisionResponse; // Added this line
            setResponse(responseData); // Changed to use the variable

            Analytics.trackResponseViewed(
              decisionId, 
              'interim', 
              responseData.participantCount // Now responseData is defined
            );
          }
        } else {
          // No synthesis exists yet
          setResponse(null);
        }
  
        // Start polling if processing
        if (responseStatus.status === 'processing') {
          setIsRefreshing(true);
          startPolling();
        }
  
      } catch (err) {
        console.error('Error loading response:', err);
        setError('Failed to load response. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
  
    loadData();
  }, [decisionId, startPolling, setCurrentDecision]);
  
  // Keep this effect for handling chat completion
  useEffect(() => {
    if (isJustCompleted && !isPolling) {
      setIsRefreshing(true);
      startPolling();
    }
  }, [isJustCompleted, isPolling, startPolling]);

  const handleCloseDecision = async () => {
    if (!decisionId) return;
    
    try {
      console.log('Closing decision:', decisionId);
      const decisionRef = doc(db, 'decisions', decisionId);
      await updateDoc(decisionRef, {
        status: 'closed',
        lastUpdated: Timestamp.now()
      });
      console.log('Decision closed successfully');

      Analytics.trackDecisionClosed(decisionId, decision?.participants || 0);
      
      // Update local state
      setDecision(prev => prev ? { ...prev, status: 'closed' } : null);
      
      // Start polling and show updating state
      setIsRefreshing(true);
      setIsPolling(true);
      startPolling();
    } catch (err) {
      console.error('Error closing decision:', err);
      setError('Failed to close decision. Please try again.');
    }
  };

  // If we're loading or waiting for synthesis after chat completion
  if (loading || (isJustCompleted && !response)) {
    return (
      <div className="container mx-auto max-w-3xl px-4">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl mb-2">
              {decision?.question}
            </CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Group Response</CardTitle>
            <CardDescription className="flex items-center space-x-2">
              <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
              Generating response...
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto max-w-3xl">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-3xl px-4">
      <Card className="mb-6">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-2xl mb-2">
              {decision?.question}
            </CardTitle>
            
            {decision?.details && (
              <div>
                {showDetails ? (
                  <>
                    <p className="text-muted-foreground">{decision.details}</p>
                    <button 
                      onClick={() => setShowDetails(false)}
                      className="text-sm text-primary hover:underline mt-2 focus:outline-none"
                    >
                      Hide details
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setShowDetails(true)}
                    className="text-sm text-primary hover:underline focus:outline-none"
                  >
                    Show details
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Show edit button if user is creator AND no participants yet */}
          {user && decision?.createdBy === user.uid && decision.participants === 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/d/${decisionId}/edit`)}
              className="flex items-center gap-1.5"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
        
        {/* Only show for users who haven't participated yet */}
        {decision?.status === 'active' && !participant && (
          <CardContent className="pt-0 pb-6">
            <div className="flex justify-center">
              <Button
                onClick={() => navigate(`/d/${decisionId}/chat`)}
                className="bg-primary hover:bg-primary/90 w-full md:w-auto"
                size="lg"
              >
                <MessageSquare className="h-5 w-5 mr-2" />
                Add Your View
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </CardContent>
        )}
      </Card>
  
      <Card>
        <CardHeader className="flex-row justify-between items-center">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <CardTitle>Group Response</CardTitle>
              {decision?.status === 'closed' ? (
                <span className="text-sm px-2 py-1 bg-primary/10 text-primary rounded-md font-medium">
                  Final Summary
                </span>
              ) : (
                // Only show "Preliminary Summary" tag if there's an actual response with enough participants
                response && response.participantCount >= 3 && (
                  <span className="text-sm px-2 py-1 bg-secondary/10 text-secondary rounded-md font-medium">
                    Preliminary Summary
                  </span>
                )
              )}
            </div>
            {response?.status === 'generating' && (
              <CardDescription>(Updating...)</CardDescription>
            )}
          </div>
          <div className="flex gap-2">
            {decision?.createdBy === user?.uid && decision?.status === 'active' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCloseDecision}
                className="text-destructive hover:bg-destructive/10"
              >
                Close Decision
              </Button>
            )}
          </div>
        </CardHeader>
          
        <CardContent>
          {!response ? (
            <div className="text-center text-muted-foreground py-8">
              <p>No response generated yet.</p>
              <p className="text-sm">Responses are generated after receiving input from participants.</p>
            </div>
          ) : response.status === 'generating' || (isPolling && isRefreshing) ? (
            <div className="space-y-6">
              <div className="flex items-center justify-center gap-3 py-8">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                <span className="text-muted-foreground">
                  {decision?.status === 'closed' ? "Generating final response..." : "Generating group response..."}
                </span>
              </div>
            </div>
          ) : response.status === 'failed' ? (
            <div className="space-y-6">
              <div className="text-center text-destructive">
                <p>Failed to generate response.</p>
                <Button 
                  onClick={() => window.location.reload()}
                  variant="outline" 
                  size="sm"
                  className="mt-2"
                >
                  Reload Page
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col gap-2 text-sm text-muted-foreground md:flex-row md:items-center">
                {response.participantCount > 0 ? (
                  <ParticipantList 
                    decisionId={decisionId || ''} 
                    totalCount={response.participantCount} 
                  />
                ) : (
                  <span className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>No active participants</span>
                  </span>
                )}
                <span className="hidden md:block">·</span>
                <span className="flex items-center gap-2">
                  <span>updated {formatTimeAgo(response.lastUpdated.toDate())}</span>
                  {isJustCompleted && isPolling && (
                    <span className="ml-2 text-primary animate-pulse">(updating...)</span>
                  )}
                  {decision?.status === 'closed' && (
                    <>
                      <span>·</span>
                      <span className="font-medium text-destructive">Decision Closed</span>
                    </>
                  )}
                </span>
              </div>
              {response.participantCount >= 3 ? (
                <>
                  <div className="prose max-w-none whitespace-pre-line">
                    {formatMessageText(response.content)}
                  </div>
                  <div className="mt-6">
                    <ResponseActions
                      decisionId={decisionId!}
                      decision={decision!}
                      responseContent={response.content}
                      participantCount={response.participantCount}
                      isComplete={decision?.status === 'closed'}
                      onShare={handleShare}
                      participant={participant} // Pass the participant prop
                    />
                  </div>
                </>
              ) : (
                <div className="text-center py-6 border border-dashed rounded-md bg-muted/30">
                  <p className="text-muted-foreground mb-2">Waiting for more participants</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    A summary of the group's views will be shown after 3 people have shared their input.
                  </p>
                  
                  {participant && (
                    <div className="flex flex-col md:flex-row justify-center gap-4">
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/d/${decisionId}/chat`)}
                        className="w-full md:w-auto"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Continue Chat
                      </Button>
                      
                      <Button 
                        onClick={handleShare}
                        className="w-full md:w-auto"
                      >
                        <Users className="h-4 w-4 mr-2" />
                        Invite More Participants
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
          
        {decision?.status === 'active' && (
          <CardFooter className="pt-4 pb-6 flex justify-center">
            {!participant && (
              <Button
                onClick={() => navigate(`/d/${decisionId}/chat`)}
                className="bg-primary hover:bg-primary/90 w-full md:w-auto"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Add Your View
              </Button>
            )}
          </CardFooter>
        )}
      </Card>
    </div>
  );
}